/**
 * Supabase Data Layer for AMETAL PCS
 * Maps localStorage arrays to Supabase tables.
 * Uses a simple approach: load all data on init, save changes per-table.
 */
import { supabase, isSupabaseEnabled } from "./supabase";

// Map from seedData key → Supabase table name
const TABLE_MAP = {
  companies: "companies",
  users: "users",
  clients: "clients",
  projects: "projects",
  warehouses: "warehouses",
  items: "items",
  vendors: "vendors",
  employees: "employees",
  boqItems: "boq_items",
  tasks: "tasks",
  purchaseOrders: "purchase_orders",
  poLineItems: "po_line_items",
  materialDeliveries: "material_deliveries",
  vendorInvoices: "vendor_invoices",
  vendorPayments: "vendor_payments",
  clientInvoices: "client_invoices",
  clientPayments: "client_payments",
  variations: "variations",
  clientDisputes: "client_disputes",
  retentionReleases: "retention_releases",
  warehouseTransactions: "warehouse_transactions",
  dailyLogs: "daily_logs",
  dailyLogTaskProgress: "daily_log_task_progress",
  laborUsage: "labor_usage",
  equipmentUsage: "equipment_usage",
  salaryCharges: "salary_charges",
  otherExpenses: "other_expenses",
  overheadCosts: "overhead_costs",
  costEstimates: "cost_estimates",
  equipment: "equipment",
  equipmentAssignments: "equipment_assignments",
  fuelConsumption: "fuel_consumption",
  maintenanceLogs: "maintenance_logs",
  equipmentRentalCharges: "equipment_rental_charges",
  subContracts: "sub_contracts",
  subClaims: "sub_claims",
  subBackCharges: "sub_back_charges",
  subPerformanceRatings: "sub_performance_ratings",
  subcontractorProfiles: "subcontractor_profiles",
  documents: "documents",
  drawings: "drawings",
  rfis: "rfis",
  submittals: "submittals",
  correspondence: "correspondence",
  safetyIncidents: "safety_incidents",
  inspectionChecklists: "inspection_checklists",
  ncrs: "ncrs",
  permitToWork: "permit_to_work",
  delayEvents: "delay_events",
  companyAccounts: "company_accounts",
  accountTransactions: "account_transactions",
  notifications: "notifications",
  todos: "todos",
  userNotes: "user_notes",
  sitePhotos: "site_photos",
  auditLog: "audit_log",
  tenders: "tenders",
  tenderItems: "tender_items",
  contracts: "contracts",
  contractMilestones: "contract_milestones",
  timesheets: "timesheets",
  timesheetLines: "timesheet_lines",
  attendanceRecords: "attendance_records",
  mtoRecords: "mto_records",
  itpRecords: "itp_records",
  punchListItems: "punch_list_items",
  quotations: "quotations",
  datasheets: "datasheets",
  otherDocs: "other_docs",
  workPermitDocs: "work_permit_docs",
  qcReportDocs: "qc_report_docs",
};

// Reverse map: table_name → camelCase key
// eslint-disable-next-line no-unused-vars
const REVERSE_MAP = Object.fromEntries(
  Object.entries(TABLE_MAP).map(([k, v]) => [v, k])
);

// Map from Supabase snake_case columns → camelCase JS fields per table
// Only for tables where the app uses different field names
// eslint-disable-next-line no-unused-vars
const FIELD_MAP = {
  boq_items: { boq_id: "boq_id" },
  purchase_orders: { po_id: "po_id" },
  po_line_items: { line_id: "line_id" },
  warehouse_transactions: { txn_id: "txn_id" },
  daily_logs: { log_id: "log_id" },
};

// ── Tiered Loading Strategy ──
// Tier 1 (Critical): Loaded immediately on startup — needed for login, navigation, dashboard
// Tier 2 (Core): Loaded after login — needed for most pages
// Tier 3 (Detail): Loaded on-demand when user visits specific pages
const TIER_1_KEYS = [
  "companies", "users", "clients", "projects", "notifications", "todos", "userNotes",
  "companyAccounts", "items", "vendors", "employees", "warehouses",
];
const TIER_2_KEYS = [
  "boqItems", "tasks", "purchaseOrders", "poLineItems", "vendorInvoices",
  "vendorPayments", "warehouseTransactions", "dailyLogs", "equipment",
  "clientInvoices", "clientPayments", "variations", "accountTransactions",
  "materialDeliveries", "subContracts",
];
// TIER_3: everything else (loaded on demand or in background)
const TIER_3_KEYS = Object.keys(TABLE_MAP).filter(
  k => !TIER_1_KEYS.includes(k) && !TIER_2_KEYS.includes(k)
);

/**
 * Load a batch of tables from Supabase
 */
// Helper: race a promise against a timeout
function withTimeout(promise, ms) {
  return Promise.race([
    promise,
    new Promise((_, reject) => setTimeout(() => reject(new Error("timeout")), ms)),
  ]);
}

async function loadTables(keys) {
  if (!isSupabaseEnabled) return {};
  const result = {};
  const entries = keys.map(k => [k, TABLE_MAP[k]]).filter(([, t]) => t);

  // Load ALL tables in parallel with 8-second timeout per table
  const promises = entries.map(async ([jsKey, tableName]) => {
    try {
      const { data, error } = await withTimeout(
        supabase.from(tableName).select("*").limit(10000),
        8000
      );

      if (error) {
        // 404/PGRST = table doesn't exist — silently skip
        if (error.code === "PGRST204" || error.code === "42P01" || error.message?.includes("does not exist")) {
          result[jsKey] = [];
        } else {
          console.warn(`Supabase load ${tableName}:`, error.message);
          result[jsKey] = [];
        }
      } else {
        result[jsKey] = data || [];
      }
    } catch (e) {
      // Timeout or network error — skip silently
      result[jsKey] = [];
    }
  });
  await Promise.all(promises);
  return result;
}

/**
 * Load all data from Supabase — all tiers in parallel for speed.
 */
export async function loadAllFromSupabase() {
  if (!isSupabaseEnabled) return null;

  const [tier1, tier2, tier3] = await Promise.all([
    loadTables(TIER_1_KEYS),
    loadTables(TIER_2_KEYS),
    loadTables(TIER_3_KEYS),
  ]);

  return { ...tier1, ...tier2, ...tier3 };
}

/**
 * Load only critical data (Tier 1) — for fast initial render
 */
export async function loadCriticalData() {
  return isSupabaseEnabled ? loadTables(TIER_1_KEYS) : null;
}

/**
 * Load remaining data (Tier 2 + 3) — call after initial render
 */
export async function loadRemainingData() {
  if (!isSupabaseEnabled) return null;
  const [tier2, tier3] = await Promise.all([
    loadTables(TIER_2_KEYS),
    loadTables(TIER_3_KEYS),
  ]);
  return { ...tier2, ...tier3 };
}

/**
 * Load data for a specific table on demand
 */
export async function loadTable(jsKey) {
  if (!isSupabaseEnabled) return [];
  const tableName = TABLE_MAP[jsKey];
  if (!tableName) return [];
  try {
    const { data, error } = await supabase.from(tableName).select("*").limit(10000);
    if (error) { console.warn(`Load ${tableName}:`, error.message); return []; }
    return data || [];
  } catch (e) { console.warn(`Load ${tableName}:`, e); return []; }
}

/**
 * Detect changes between old and new data, then sync to Supabase.
 * Uses a simple diff: compare arrays by primary key.
 */
// Tables that do NOT have a company_id column
const NO_COMPANY_ID_TABLES = new Set(["companies", "auditLog"]);

// Known DB columns per table — only these fields are sent to Supabase.
// Any extra fields (form state, UI helpers) are stripped automatically.
const TABLE_COLUMNS = {
  companies: ["company_id", "name", "code", "logo_text", "color", "plan", "users_limit", "created_at"],
  users: ["user_id", "company_id", "name", "email", "role", "salt", "hashed_password", "password", "assigned_project_ids", "is_active", "last_login", "created_at"],
  clients: ["client_id", "company_id", "name", "contact_person", "phone", "email", "address", "city", "country", "tax_registration_number", "payment_terms", "currency", "notes", "is_active", "created_at"],
  projects: ["project_id", "company_id", "name", "client", "client_id", "contract_value", "start_date", "expected_finish_date", "actual_finish_date", "status", "notes", "management_type", "project_manager_id", "supervisor_id", "warehouse_id", "telegram_chat_id", "created_at"],
  warehouses: ["warehouse_id", "company_id", "name", "location", "type", "project_id", "is_active", "created_at"],
  items: ["item_id", "company_id", "name", "code", "category", "unit", "min_stock", "is_active", "created_at"],
  vendors: ["vendor_id", "company_id", "name", "vendor_name", "contact_person", "email", "phone", "address", "category", "is_active", "rating", "created_at"],
  employees: ["employee_id", "company_id", "name", "position", "department", "daily_rate", "is_active", "project_id", "created_at"],
  boqItems: ["boq_id", "company_id", "project_id", "code", "description", "unit", "quantity", "rate", "cost_type", "parent_code", "is_summary_row", "work_permit", "qc_before", "qc_after"],
  tasks: ["task_id", "company_id", "project_id", "task_name", "total_qty", "unit", "completed_qty", "planned_start", "planned_finish", "actual_start", "actual_finish", "duration_days", "weight_pct", "actual_progress_pct", "linked_boq_ids", "sort_order", "predecessors", "wbs_code"],
  purchaseOrders: ["po_id", "company_id", "project_id", "po_number", "vendor_id", "issue_date", "expected_delivery", "status", "total_amount", "notes", "approved_by", "approved_at", "created_at"],
  poLineItems: ["line_id", "company_id", "po_id", "item_id", "item_code", "item_name", "description", "quantity", "unit", "unit_price", "total_price", "qty_received"],
  vendorInvoices: ["invoice_id", "company_id", "vendor_id", "po_id", "project_id", "invoice_number", "amount", "date", "due_date", "status", "notes", "created_at"],
  vendorPayments: ["payment_id", "company_id", "invoice_id", "vendor_id", "amount", "date", "method", "reference", "notes", "created_at"],
  warehouseTransactions: ["txn_id", "company_id", "warehouse_id", "item_id", "type", "quantity", "date", "reference", "notes", "project_id", "po_id", "created_at"],
  dailyLogs: ["log_id", "company_id", "project_id", "date", "weather", "temperature", "wind", "notes", "safety_incidents", "visitors", "created_by", "created_at"],
  equipment: ["equipment_id", "company_id", "name", "type", "model", "serial_number", "status", "location", "project_id", "daily_rate", "purchase_date", "created_at"],
  notifications: ["id", "company_id", "to_user_id", "from_user_id", "type", "title", "message", "page_link", "project_id", "is_read", "created_at"],
  todos: ["id", "company_id", "user_id", "title", "description", "status", "priority", "due_date", "project_id", "created_at"],
  userNotes: ["note_id", "company_id", "user_id", "title", "content", "created_at", "updated_at"],
  companyAccounts: ["account_id", "company_id", "name", "type", "bank_name", "account_number", "currency", "opening_balance", "is_active", "created_at"],
  accountTransactions: ["txn_id", "company_id", "type", "from_account", "to_account", "amount", "date", "reference", "notes", "entity_type", "entity_id", "created_by", "created_at"],
  documents: ["doc_id", "company_id", "project_id", "title", "category", "file_url", "notes", "uploaded_by", "created_at"],
  drawings: ["drawing_id", "company_id", "project_id", "drawing_number", "title", "discipline", "revision", "status", "date", "revision_date", "issued_by", "file_url", "notes", "created_at"],
  rfis: ["rfi_id", "company_id", "project_id", "rfi_number", "subject", "description", "raised_by", "assigned_to", "issued_to", "date_raised", "date_issued", "date_required", "date_responded", "status", "response", "priority", "file_url", "created_at"],
  submittals: ["submittal_id", "company_id", "project_id", "submittal_number", "title", "description", "submitted_by", "submission_date", "date_submitted", "status", "review_date", "reviewed_by", "revision", "vendor_id", "notes", "file_url", "created_at"],
  correspondence: ["correspondence_id", "company_id", "project_id", "ref_number", "subject", "from_party", "to_party", "date", "type", "direction", "status", "file_url", "notes", "created_at"],
  sitePhotos: ["photo_id", "company_id", "project_id", "date", "description", "location", "url", "thumbnail_url", "uploaded_by", "tags", "created_at"],
  quotations: ["quotation_id", "company_id", "project_id", "vendor_id", "quote_number", "subject", "amount", "date", "validity", "status", "file_url", "notes", "created_at"],
  datasheets: ["datasheet_id", "company_id", "project_id", "vendor_id", "title", "category", "material", "date", "file_url", "notes", "created_at"],
  otherDocs: ["other_doc_id", "company_id", "project_id", "title", "category", "date", "file_url", "notes", "created_at"],
  workPermitDocs: ["doc_id", "company_id", "project_id", "boq_id", "permit_type", "document_ref", "file_url", "file_name", "uploaded_by", "uploaded_date", "notes", "status"],
  qcReportDocs: ["doc_id", "company_id", "project_id", "boq_id", "qc_type", "report_name", "document_ref", "file_url", "file_name", "uploaded_by", "uploaded_date", "notes", "status"],
};

/**
 * Strip a record down to only known DB columns for a given table.
 * Unknown fields (form helpers, UI state) are removed to avoid 400 errors.
 */
function stripToColumns(jsKey, record) {
  const cols = TABLE_COLUMNS[jsKey];
  if (!cols) {
    // No whitelist — still clean empty strings to null
    const clean = { ...record };
    for (const key of Object.keys(clean)) {
      if (clean[key] === "") clean[key] = null;
    }
    return clean;
  }
  const clean = {};
  for (const key of cols) {
    if (key in record) {
      // Convert empty strings to null — PostgreSQL can't cast "" to integer/numeric
      clean[key] = record[key] === "" ? null : record[key];
    }
  }
  return clean;
}

export async function syncToSupabase(oldData, newData) {
  if (!isSupabaseEnabled) return;

  // Determine company_id from the data (used to inject into records missing it)
  const companyId = newData.companies?.[0]?.company_id || "c1";

  const changedKeys = Object.keys(TABLE_MAP).filter(jsKey => {
    return newData[jsKey] !== oldData[jsKey];
  });

  if (changedKeys.length === 0) return;

  const promises = changedKeys.map(async (jsKey) => {
    const tableName = TABLE_MAP[jsKey];
    const oldArr = oldData[jsKey] || [];
    const newArr = newData[jsKey] || [];

    // Determine the primary key field for this table
    const pk = getPrimaryKey(jsKey);

    const oldMap = new Map(oldArr.map(r => [r[pk], r]));
    const newMap = new Map(newArr.map(r => [r[pk], r]));

    // Auto-inject company_id into records that are missing it
    const needsCompanyId = !NO_COMPANY_ID_TABLES.has(jsKey);
    const ensureCompanyId = (record) => {
      if (needsCompanyId && !record.company_id) {
        return { ...record, company_id: companyId };
      }
      return record;
    };

    // Find inserts (in new but not in old) — strip unknown columns + ensure company_id
    const inserts = newArr.filter(r => r[pk] && !oldMap.has(r[pk]))
      .map(ensureCompanyId).map(r => stripToColumns(jsKey, r));

    // Find updates (in both but changed) — strip unknown columns + ensure company_id
    const updates = newArr.filter(r => {
      if (!r[pk] || !oldMap.has(r[pk])) return false;
      return JSON.stringify(r) !== JSON.stringify(oldMap.get(r[pk]));
    }).map(ensureCompanyId).map(r => stripToColumns(jsKey, r));

    try {
      // Batch inserts
      if (inserts.length > 0) {
        const { error } = await supabase.from(tableName).upsert(inserts, { onConflict: pk });
        if (error) console.warn(`Insert ${tableName}:`, error.message);
      }

      // Batch updates
      if (updates.length > 0) {
        const { error } = await supabase.from(tableName).upsert(updates, { onConflict: pk });
        if (error) console.warn(`Update ${tableName}:`, error.message);
      }

      // NOTE: Auto-delete disabled — was causing data loss on reload.
      // When the app loads with empty seed arrays and Supabase has real data,
      // the diff saw "old has records, new is empty" and deleted everything.
      // Deletes are now only handled via explicit user actions in the UI.
    } catch (e) {
      console.warn(`Sync ${tableName} failed:`, e);
    }
  });

  await Promise.all(promises);
}

/**
 * Get primary key field name for a given JS data key
 */
function getPrimaryKey(jsKey) {
  const pkMap = {
    companies: "company_id",
    users: "user_id",
    clients: "client_id",
    projects: "project_id",
    warehouses: "warehouse_id",
    items: "item_id",
    vendors: "vendor_id",
    employees: "employee_id",
    boqItems: "boq_id",
    tasks: "task_id",
    purchaseOrders: "po_id",
    poLineItems: "line_id",
    materialDeliveries: "delivery_id",
    vendorInvoices: "invoice_id",
    vendorPayments: "payment_id",
    clientInvoices: "invoice_id",
    clientPayments: "payment_id",
    variations: "variation_id",
    clientDisputes: "dispute_id",
    retentionReleases: "release_id",
    warehouseTransactions: "txn_id",
    dailyLogs: "log_id",
    dailyLogTaskProgress: "id",
    laborUsage: "id",
    equipmentUsage: "id",
    salaryCharges: "id",
    otherExpenses: "id",
    overheadCosts: "id",
    costEstimates: "id",
    equipment: "equipment_id",
    equipmentAssignments: "assignment_id",
    fuelConsumption: "id",
    maintenanceLogs: "id",
    equipmentRentalCharges: "id",
    subContracts: "contract_id",
    subClaims: "claim_id",
    subBackCharges: "id",
    subPerformanceRatings: "id",
    subcontractorProfiles: "id",
    documents: "doc_id",
    drawings: "drawing_id",
    rfis: "rfi_id",
    submittals: "submittal_id",
    correspondence: "correspondence_id",
    safetyIncidents: "incident_id",
    inspectionChecklists: "checklist_id",
    ncrs: "ncr_id",
    permitToWork: "permit_id",
    delayEvents: "event_id",
    companyAccounts: "account_id",
    accountTransactions: "txn_id",
    notifications: "id",
    todos: "id",
    userNotes: "note_id",
    sitePhotos: "photo_id",
    auditLog: "id",
    tenders: "tender_id",
    tenderItems: "item_id",
    contracts: "contract_id",
    contractMilestones: "milestone_id",
    timesheets: "timesheet_id",
    timesheetLines: "line_id",
    attendanceRecords: "id",
    mtoRecords: "id",
    itpRecords: "id",
    punchListItems: "id",
    quotations: "quotation_id",
    datasheets: "datasheet_id",
    otherDocs: "other_doc_id",
    workPermitDocs: "doc_id",
    qcReportDocs: "doc_id",
  };
  return pkMap[jsKey] || "id";
}

/**
 * Check if Supabase tables exist and are accessible
 */
export async function testSupabaseConnection() {
  if (!isSupabaseEnabled) return { ok: false, error: "Supabase not configured" };
  try {
    const { data, error } = await supabase.from("companies").select("company_id").limit(1);
    if (error) return { ok: false, error: error.message };
    return { ok: true, count: data?.length || 0 };
  } catch (e) {
    return { ok: false, error: e.message };
  }
}
